import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography
} from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from '../../../ui-component/FormikFormLibrary/FormikTypes';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getApplicationFee } from '../common/getFeesForCurrentPermit';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { fileUpload } from '../common/uploadPDF';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import { RootState } from 'src/redux-store/appStore';
import { Container } from '@mui/system';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import PdfGenerator from 'src/ui-component/PdfGenerator';

const NewApplication1: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permit } = useParams();

  const currentPermitDetails = useSelector(
    (store: RootState) => store.currentPermit.currentPermitDetails
  );
  const userData = useSelector((store: RootState) => store.user.userDetails);

  const basePath = `/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}`;

  const [roadOpeningFormData, setRoadOpeningFormData] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isFeesApplicable, setIsFeesApplicable] = useState<boolean>(false);
  const [RoadOpeningLocationOptions, setRoadOpeningLocationOptions] = useState<
    string[]
  >([]);
  const [clickedOn, setClickedOn] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [taxRate, setTaxRate] = useState<number>(0);
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [applicableFee, setApplicableFee] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });

  // Fetching Fee Details
  useEffect(() => {
    const getFeeDetails = async (permitId) => {
      setLoading(true);
      const feeData = await getApplicationFee(permitId);
      setLoading(false);
      setTaxRate(feeData?.CreditCardFee || 0);
      setFeeDetails({
        applicationFee: Number(feeData?.ApplicationFee),
        inspectionFee: Number(feeData?.InspectionFee)
      });
    };

    getFeeDetails(permit);
  }, []);

  // Fetching Road Opening Location
  const handleRoadOpeningLocation = async (val) => {
    if (val) {
      if (typeof val === 'string' && val.trim().length < 3) return;
      if (val?.PropAddress) {
        setRoadOpeningFormData({
          ...roadOpeningFormData,
          RoadOpeningLocation: val?.PropAddress
        });
      } else {
        const response = await httpRequest(
          'GET',
          `/propertyDetails/searchBlockLots_Address?AddressPrefix=${val}`
        );
        if (response?.status === 'success') {
          const list = response?.data
            ?.map((value: { PropAddress: string }) => {
              return { ...value, label: value?.PropAddress };
            })
            ?.slice(0, 10);
          setRoadOpeningLocationOptions(list);
        } else if (response?.type === 'NETWORK_ERROR') {
        } else {
          toast.error(response?.message);
          dispatch(handleError(response?.message));
        }
      }
    }
  };

  // Handling Change in Fields
  const handleChange = (val, e) => {
    const { name } = e;
    if (name === 'ApplicationTitle') {
      const feeApplicableOptions = [
        'Home Owner',
        'Utility Company',
        'Public Service Electric & Gas(PSE&G)'
      ];
      setIsFeesApplicable(feeApplicableOptions.includes(val));

      let currentApplicationFee = 0;
      let currentInspectionFee = 0;

      if (val === feeApplicableOptions[0] || val === feeApplicableOptions[2]) {
        currentApplicationFee = feeDetails.applicationFee;
      } else if (val == feeApplicableOptions[1]) {
        currentApplicationFee = feeDetails.applicationFee;
        currentInspectionFee = feeDetails.inspectionFee;
      }
      setApplicableFee({
        applicationFee: currentApplicationFee,
        inspectionFee: currentInspectionFee
      });
      setRoadOpeningFormData({
        ...roadOpeningFormData,
        [name]: val
      });
    } else if (name === 'RoadOpeningLocation') {
      handleRoadOpeningLocation(val);
    } else {
      setRoadOpeningFormData({
        ...roadOpeningFormData,
        [name]: val
      });
    }
  };

  // Handling Date Change
  const handleDateChange = (val) => {
    setRoadOpeningFormData({
      ...roadOpeningFormData,
      DateWorkIsScheduledToBegin: val
    });
  };

  // Handling File Upload
  const handleFileUpload = async (file) => {
    if (file.length) {
      const uploadedFileName = await fileUpload(file, 'Road');
      setRoadOpeningFormData({
        ...roadOpeningFormData,
        DocumentFileUpload: uploadedFileName,
        mapFile: file
      });
    } else {
      setRoadOpeningFormData({
        ...roadOpeningFormData,
        DocumentFileUpload: '',
        mapFile: []
      });
    }
  };

  // Handling Check Error
  const handleCheckError = () => {
    setError('Please select the checkbox to continue.');
  };

  // Handling Form Submit
  const handleSubmit = async (data) => {
    if (!roadOpeningFormData?.CodeChapter435Agreement) {
      handleCheckError();
      return;
    }
    setLoading(true);
    const requestData = { ...roadOpeningFormData, ...data };
    const payload = {
      ApplicantType: requestData?.ApplicationTitle,
      ApplicantName: requestData?.ApplicantName,
      ApplicantPhoneNo: requestData?.PhoneNumber,
      Contractor: requestData?.Contractor || null,
      ApplicantAddress: requestData?.ApplicantAddress,
      ContractorAddress: requestData?.ContractorAddress || null,
      RoadOpeningLocation: requestData?.RoadOpeningLocation,
      WorkDate: requestData?.DateWorkIsScheduledToBegin,
      FtWide: requestData?.dimensionWidth,
      FtLong: requestData?.dimensionLength,
      FtDeep: requestData?.dimensionDepth,
      TotalSqFootage: requestData?.dimensionTotal,
      Emergency: requestData?.EmergencyRoadOpening === 'Yes' ? 1 : 0,
      Purpose: requestData?.Purpose || null,
      DocumentFileUpload:
        requestData?.DocumentFileUpload || requestData?.mapFile[0]?.name,
      ApplicationFee:
        applicableFee.applicationFee > 0 ? applicableFee.applicationFee : null,
      EscrowFee:
        applicableFee.inspectionFee > 0 ? applicableFee.inspectionFee : null,
      ApplicationStatus:
        clickedOn === 'Save Draft' || isFeesApplicable ? 'Draft' : 'In Review'
    };
    setLoading(true);
    const response = await httpRequest(
      'POST',
      '/road_permit_application/insert',
      payload
    );

    if (response?.status === 'success') {
      toast.success(response.message);
      if (clickedOn === 'Save Draft') navigate(`${basePath}/draft-application`);
      else if (!isFeesApplicable) navigate(`${basePath}/application-list`);
      else {
        const paymentSummary: PaymentSummary = {
          fees: Object.fromEntries(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            Object.entries(applicableFee).filter(([_, value]) => value > 0)
          ),
          ...(taxRate > 0 ? { taxRate } : {})
        };

        const paymentApplicationDetails: PaymentApplicationDetails = {
          applicationDetails: {
            userName: `${userData?.FirstName} ${userData?.LastName}`,
            applicationNumber: null,
            applicationId: response?.data?.ApplicationID,
            applicationStatus: 'Draft',
            statusPayload: 'In Review',
            permitPrefix: 'Road'
          }
        };
        navigate(`/payment-methods`, {
          state: { paymentSummary, paymentApplicationDetails }
        });
      }
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setLoading(false);
  };

  // Get Total Square Footage
  const getTotalSquareFt = () => {
    return isNaN(
      parseFloat(roadOpeningFormData?.dimensionWidth) *
        parseFloat(roadOpeningFormData?.dimensionLength)
    )
      ? '0'
      : (
          parseFloat(roadOpeningFormData?.dimensionWidth) *
          parseFloat(roadOpeningFormData?.dimensionLength)
        ).toString();
  };

  // All Fields using for Formik Form 
  const allFields: Fields[] = [
    {
      fieldName: 'ApplicationTitle',
      fieldLabel: 'Application Title',
      fieldType: 'select',
      options: [
        'Home Owner',
        'Utility Company',
        'Cable Company',
        'New Jarsey Natural Gas(NJNG)',
        'Public Service Electric & Gas(PSE&G)'
      ],
      required: true,
      initialValue: roadOpeningFormData?.ApplicationTitle || '',
      dependency: handleChange
    },
    {
      fieldName: 'ApplicantName',
      fieldLabel: 'Applicant Name',
      fieldType: 'text',
      required: true,
      initialValue: roadOpeningFormData?.ApplicantName || '',
      dependency: handleChange
    },
    {
      fieldName: 'PhoneNumber',
      fieldLabel: 'Phone Number',
      fieldType: 'mobile',
      required: true,
      initialValue: roadOpeningFormData?.PhoneNumber || '',
      dependency: handleChange
    },
    {
      fieldName: 'Contractor',
      fieldLabel: 'Contractor',
      fieldType: 'text',
      initialValue: roadOpeningFormData?.Contractor || '',
      dependency: handleChange
    },
    {
      fieldName: 'ApplicantAddress',
      fieldLabel: 'Applicant Address',
      fieldType: 'note',
      required: true,
      initialValue: roadOpeningFormData?.ApplicantAddress || '',
      dependency: handleChange
    },
    {
      fieldName: 'ContractorAddress',
      fieldLabel: 'Contractor Address',
      fieldType: 'note',
      initialValue: roadOpeningFormData?.ContractorAddress || '',
      dependency: handleChange
    },
    {
      fieldName: 'RoadOpeningLocation',
      fieldLabel: 'Road Opening Location',
      fieldType: 'autocomplete',
      placeHolder: 'Enter minimum 3 digits',
      isErrorField: false,
      initialValue: roadOpeningFormData?.RoadOpeningLocation,
      required: true,
      disabled: false,
      options: RoadOpeningLocationOptions,
      dependency: handleChange
    },
    {
      fieldName: 'DateWorkIsScheduledToBegin',
      fieldLabel: 'Date Work is Scheduled to Begin',
      fieldType: 'futureDate',
      isErrorField: false,
      required: true,
      initialValue: roadOpeningFormData?.DateWorkIsScheduledToBegin || '',
      dependency: handleDateChange
    },
    {
      fieldName: 'dimensionWidth',
      fieldLabel: 'Width (ft)',
      fieldType: 'number',
      heading: 'Road Opening Dimensions:',
      required: true,
      initialValue: roadOpeningFormData?.dimensionWidth,
      dependency: handleChange
    },
    {
      fieldName: 'dimensionLength',
      fieldLabel: 'Length (ft)',
      fieldType: 'number',
      required: true,
      initialValue: roadOpeningFormData?.dimensionLength,
      dependency: handleChange
    },
    {
      fieldName: 'dimensionDepth',
      fieldLabel: 'Depth (ft)',
      fieldType: 'number',
      required: true,
      initialValue: roadOpeningFormData?.dimensionDepth,
      dependency: handleChange
    },
    {
      fieldName: 'dimensionTotal',
      fieldLabel: 'Total Square Ft.',
      fieldType: 'number',
      initialValue: getTotalSquareFt(),
      disabled: true,
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'EmergencyRoadOpening',
      fieldLabel: 'Emergency Road Opening?',
      fieldType: 'radio',
      isErrorField: false,
      required: true,
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' }
      ],
      disabled: false,
      initialValue: roadOpeningFormData?.EmergencyRoadOpening || false,
      dependency: handleChange
    },
    {
      fieldName: 'Purpose',
      fieldLabel: 'Purpose',
      fieldType: 'note',
      initialValue: roadOpeningFormData?.Purpose || '',
      dependency: handleChange
    },
    {
      fieldName: 'mapFile',
      fieldLabel: 'Upload Document',
      fieldType: 'file',
      isErrorField: false,
      required: true,
      listUI: true,
      placement: 'start',
      multiSelect: false,
      disabled: false,
      aboveParagraph: `Please upload a sketch that shows the location of the proposed road opening. Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF files are allowed, with maximum file size of 10MB.`,
      initialValue: roadOpeningFormData?.mapFile || [],
      dependency: handleFileUpload
    }
  ];

  const BottomSection = () => {
    return (
      <>
        {applicableFee.applicationFee > 0 && (
          <TextField
            id="outlined-basic"
            value={`$${feeDetails?.applicationFee}`}
            disabled
            sx={{ mt: 2 }}
            label="Application Fee"
            variant="outlined"
            InputLabelProps={{
              className: 'weight-600'
            }}
          />
        )}
        {applicableFee.inspectionFee > 0 && (
          <TextField
            id="outlined-basic"
            value={`$${feeDetails?.inspectionFee}`}
            disabled
            sx={{ mt: 2, ml: 2 }}
            label="Inspection Fee"
            variant="outlined"
            InputLabelProps={{
              className: 'weight-600'
            }}
          />
        )}
        {
          <Box mt={2}>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    // required
                    name="CodeChapter435Agreement"
                    // value={treePermitFormData[data?.name]}
                    checked={roadOpeningFormData?.CodeChapter435Agreement}
                    color="primary"
                    onChange={(e) => {
                      setError('');
                      setRoadOpeningFormData({
                        ...roadOpeningFormData,
                        ['CodeChapter435Agreement']: e.target.checked
                      });
                      //   setError('');
                    }}
                  />
                }
                label={
                  <>
                    I have read and understand Township Code Chapter 435{' '}
                    <a
                      href={`https://ecode360.com/BL0918`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: 'blue' }}
                      onMouseEnter={(e) =>
                        ((e.target as HTMLAnchorElement).style.textDecoration =
                          'underline')
                      }
                      onMouseLeave={(e) =>
                        ((e.target as HTMLAnchorElement).style.textDecoration =
                          'none')
                      }
                    >
                      &quot;Streets and Sidewalks&quot;
                    </a>
                    .
                  </>
                }
              />
            </Box>
            <Typography color={'#8B0000'} mt={2}>
              {error}
            </Typography>
          </Box>
        }
      </>
    );
  };

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb PermitName={'Road Opening'} PageTitle={'New Application'} />
      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType="new"
      />
      <Card>
        <Box
          m={{ md: 3, xs: 1 }}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <PdfGenerator PermitName={'Road Opening'} allFields={allFields} />
        </Box>
        <Box m={{ md: 3, xs: 1 }} className="road_openning_edit_form">
          <FormikForm
            allFields={allFields}
            buttonTitle={
              isFeesApplicable
                ? ['Cancel', 'Save Draft', 'Proceed to Payment']
                : ['Cancel', 'Save Draft', 'Submit']
            }
            fullScreen={false}
            buttonPlacement={'right'}
            formMethod={handleSubmit}
            clickProp
            setClickedOn={setClickedOn}
            content={BottomSection}
            handleOtherButton={() => {
              navigate(`${basePath}/application-list`);
            }}
          />
        </Box>
      </Card>
    </Container>
  );
};

export default NewApplication1;
